import { MIN_PASSWORD_LENGTH } from '@sigora/domain';
import { type FormEvent, useState } from 'react';

import { confirmPasswordReset, requestPasswordReset } from '../api/client.ts';
import { RouteLink } from '../studies/RouteLink.tsx';
import type { Route } from '../studies/routing.ts';
import { CheckEmailNotice } from './CheckEmailNotice.tsx';
import { EmailRequestForm } from './EmailRequestForm.tsx';
import { failureMessage, isUnusableLink, refusedField } from './failure.ts';
import { useFragmentToken } from './fragment-token.ts';
import { passwordProblem } from './validation.ts';

export interface ResetPasswordViewProps {
  onNavigate: (route: Route) => void;
  /**
   * Called once the new password is set. The response has signed this browser in — and every
   * other browser on the account out — so the shell reads the session it now has.
   */
  onSignedIn: () => void;
}

interface FieldErrors {
  password?: string;
  confirm?: string;
}

/**
 * Where a reset link lands: a new password, typed twice, spent against the link by the submit.
 *
 * It is the one account link that signs the browser in, because setting the password is the proof
 * a sign-in asks for. A link that cannot be used offers to send a new one.
 */
export const ResetPasswordView = ({ onNavigate, onSignedIn }: ResetPasswordViewProps) => {
  const token = useFragmentToken();

  // Keyed by the token, so a second link opened in this tab gets a fresh form.
  return (
    <ResetPasswordLink
      key={token ?? ''}
      token={token}
      onNavigate={onNavigate}
      onSignedIn={onSignedIn}
    />
  );
};

const ResetPasswordLink = ({
  token,
  onNavigate,
  onSignedIn,
}: ResetPasswordViewProps & { token: string | null }) => {
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  if (sentTo !== null) {
    return (
      <main className="login" data-test-id="reset-password-view">
        <CheckEmailNotice email={sentTo} sendAgain={requestPasswordReset} onNavigate={onNavigate} />
      </main>
    );
  }

  if (invalid || token === null) {
    return (
      <main className="login" data-test-id="reset-password-view">
        <h1>This link cannot be used</h1>
        <p data-test-id="reset-password-invalid">
          Reset links work once, for a limited time. Enter your e-mail address and we will send a
          new one.
        </p>
        <EmailRequestForm
          testIdPrefix="forgot-password"
          submitLabel="Send a new link"
          pendingLabel="Sending…"
          send={requestPasswordReset}
          onSent={setSentTo}
        />
        <p className="login-links">
          <RouteLink
            to={{ name: 'studies' }}
            onNavigate={onNavigate}
            testId="reset-password-sign-in"
          >
            Back to sign in
          </RouteLink>
        </p>
      </main>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const found: FieldErrors = {};
    const problem = passwordProblem(password);
    if (problem !== null) {
      found.password = problem;
    } else if (confirm !== password) {
      found.confirm = 'The two passwords do not match.';
    }
    setFieldErrors(found);
    if (found.password !== undefined || found.confirm !== undefined) {
      return;
    }

    setPending(true);
    void confirmPasswordReset(token, password).then(
      () => {
        // Left pending: the shell replaces this view.
        onSignedIn();
      },
      (failure: unknown) => {
        setPending(false);

        if (isUnusableLink(failure)) {
          setInvalid(true);

          return;
        }

        // The password is checked before the token is spent, so the same link takes the retry.
        if (refusedField(failure) === 'password') {
          setFieldErrors({ password: failureMessage(failure) });

          return;
        }

        setError(failureMessage(failure));
      },
    );
  };

  return (
    <main className="login" data-test-id="reset-password-view">
      <h1>Choose a new password</h1>
      <p className="login-note">Setting it signs you out everywhere else.</p>
      <form onSubmit={submit}>
        <label htmlFor="reset-password-new">New password</label>
        <input
          id="reset-password-new"
          data-test-id="reset-password-new"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          aria-invalid={fieldErrors.password !== undefined}
          aria-describedby={
            fieldErrors.password === undefined ? 'reset-password-hint' : 'reset-password-new-error'
          }
          required
        />
        <p className="login-note" id="reset-password-hint">
          At least {MIN_PASSWORD_LENGTH} characters.
        </p>
        {fieldErrors.password === undefined ? null : (
          <p
            className="login-error"
            id="reset-password-new-error"
            data-test-id="reset-password-new-error"
            role="alert"
          >
            {fieldErrors.password}
          </p>
        )}

        <label htmlFor="reset-password-confirm">Type it again</label>
        <input
          id="reset-password-confirm"
          data-test-id="reset-password-confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          aria-invalid={fieldErrors.confirm !== undefined}
          aria-describedby={
            fieldErrors.confirm === undefined ? undefined : 'reset-password-confirm-error'
          }
          required
        />
        {fieldErrors.confirm === undefined ? null : (
          <p
            className="login-error"
            id="reset-password-confirm-error"
            data-test-id="reset-password-confirm-error"
            role="alert"
          >
            {fieldErrors.confirm}
          </p>
        )}

        {error === null ? null : (
          <p className="login-error" data-test-id="reset-password-error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" data-test-id="reset-password-submit" disabled={pending}>
          {pending ? 'Setting your password…' : 'Set password and sign in'}
        </button>
      </form>
    </main>
  );
};
